import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { listPatients, listAlerts, getDevice } from '../lib/adapters';
import { isImeiType } from '../lib/imei';
import { batteryColor } from '../lib/devices';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { StatusChip } from '../components/StatusChip';
import { SignalBars } from '../components/SignalBars';
import { DeviceLocationMap } from '../components/DeviceLocationMap';
import { AlertDrawer } from '../components/AlertDrawer';
import { useRelativeTime } from '../lib/time';
import { useAlertText } from '../lib/alertText';
import { getPatientPresence, ownPresenceDevices, getRoomResidentCount } from '../lib/movement';
import { MovementOverview } from '../components/movement/MovementOverview';
import { HeartStraight, WarningCircle, Lightning, Clock, CaretRight, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function FamilyHome() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const rel = useRelativeTime();
  const alertText = useAlertText();
  const alertTextRef = useRef(alertText);
  alertTextRef.current = alertText;
  const [residents, setResidents] = useState([]);
  const [alertsByRes, setAlertsByRes] = useState({});
  const [devices, setDevices] = useState({});
  const [presence, setPresence] = useState({});
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);
  const [banner, setBanner] = useState(null); // { name, id } of a newly-opened alert
  const openIdsRef = useRef(null); // baseline of open-alert ids to detect new ones

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ items: pats }, { items: alerts }] = await Promise.all([
        listPatients({ limit: 50 }),
        listAlerts({ limit: 200 }),
      ]);
      const byRes = {};
      (alerts || []).forEach((a) => {
        const k = a.resident_uuid;
        if (!byRes[k]) byRes[k] = [];
        byRes[k].push(a);
      });

      // Gentle notify: only ping for genuinely NEW (unhandled) alerts that
      // opened since we started watching. Acknowledged alerts are already
      // being handled by a caregiver and must not alarm the family.
      const openIds = (alerts || []).filter((a) => a.status === 'new').map((a) => a.id);
      if (openIdsRef.current === null) {
        openIdsRef.current = new Set(openIds);
      } else {
        const fresh = openIds.filter((id) => !openIdsRef.current.has(id));
        openIdsRef.current = new Set(openIds);
        if (fresh.length) {
          const a = (alerts || []).find((x) => x.id === fresh[0]);
          const name = a?.resident_name || '';
          toast.warning(`${name} ${t('family_status_attention')}`, { description: alertTextRef.current(a) });
          setBanner({ id: fresh[0], name });
        }
      }

      const devMap = {};
      const presMap = {};
      await Promise.all((pats || []).map(async (p) => {
        try {
          const { data } = await api.get(`/patients/${p.id}/devices`);
          const ev = (Array.isArray(data) ? data : []).find((d) => isImeiType(d.type));
          if (ev) {
            try { devMap[p.id] = await getDevice(ev.id); } catch { devMap[p.id] = ev; }
          }
        } catch { /* ignore */ }
        try {
          const pr = await getPatientPresence(p.id);
          // Family never sees shared-room movement (roommates' privacy).
          const own = ownPresenceDevices(pr, pr?.shared_room == null ? await getRoomResidentCount(pr?.room_id, { failClosed: true }) : 0).devices;
          if (own.length) presMap[p.id] = own;
        } catch { /* ignore */ }
      }));
      setResidents(pats || []);
      setAlertsByRes(byRes);
      setDevices(devMap);
      setPresence(presMap);
    } finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Live poll every 15s (paused when tab hidden) so a newly-opened alert
  // surfaces gently while a relative is watching.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstName = (user?.name || '').split(' ')[0];
  const solo = residents.length === 1;

  return (
    <div className="max-w-3xl mx-auto" data-testid="family-home">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-sage-600 mb-2">
          <HeartStraight size={22} weight="fill" />
          <span className="text-[11px] uppercase tracking-[0.24em] font-semibold">{t('family_home_kicker')}</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink">
          {firstName ? `${t('family_greeting')}, ${firstName}` : t('family_home_title')}
        </h1>
        <p className="text-ink-soft mt-3 text-base">{t('family_home_sub')}</p>
      </header>

      {banner && (
        <button
          onClick={() => { setDrawerId(banner.id); setBanner(null); }}
          data-testid="family-new-alert-banner"
          className="w-full mb-6 flex items-center gap-3 rounded-2xl border border-terracotta-200 bg-terracotta-50 px-5 py-4 text-left animate-slide-in-right"
        >
          <WarningCircle size={22} weight="fill" className="text-terracotta-600 shrink-0 animate-pulse-soft" />
          <span className="flex-1 text-sm text-terracotta-700">
            <span className="font-semibold">{banner.name} {t('family_status_attention')}.</span> {t('family_view_details')}.
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setBanner(null); }}
            className="w-7 h-7 rounded-lg grid place-items-center text-terracotta-600 hover:bg-terracotta-100"
            data-testid="family-banner-dismiss"
          >
            <X size={15} weight="bold" />
          </span>
        </button>
      )}

      {loading ? (
        <div className="text-ink-muted py-16 text-center">{t('loading')}</div>
      ) : residents.length === 0 ? (
        <div className="text-ink-muted py-16 text-center">{t('no_results')}</div>
      ) : (
        <div className="space-y-8">
          {residents.map((p) => {
            const ras = (alertsByRes[p.id] || []).slice()
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            const latest = ras[0];
            const dev = devices[p.id];
            // Headline mirrors the caregiver Patients page (authoritative
            // backend patient status) so the two views never disagree. An
            // acknowledged alert means a caregiver is already handling it and
            // must not by itself read as "requires attention" here.
            const attention = (p.status || 'ok') !== 'ok';
            return (
              <section
                key={p.id}
                data-testid={`family-card-${p.id}`}
                className="bg-white rounded-3xl border border-stone_line shadow-soft overflow-hidden animate-fade-up"
              >
                {/* Status banner */}
                <div className={`${solo ? 'px-8 py-10 md:py-12' : 'px-8 py-7'} ${attention ? 'bg-terracotta-50' : 'bg-sage-50'}`}>
                  <div className="flex items-center gap-3">
                    {attention
                      ? <WarningCircle size={solo ? 40 : 30} weight="fill" className="text-terracotta-600 shrink-0" />
                      : <HeartStraight size={solo ? 40 : 30} weight="fill" className="text-sage-600 shrink-0" />}
                    <div>
                      <h2 className={`font-heading text-ink leading-tight ${solo ? 'text-3xl md:text-5xl' : 'text-2xl md:text-3xl'}`}>
                        {attention ? `${p.name} ${t('family_status_attention')}` : `${p.name} ${t('family_status_ok')}`}
                      </h2>
                      <p className={`mt-1 ${solo ? 'text-base' : 'text-sm'} ${attention ? 'text-terracotta-700' : 'text-sage-700'}`}>
                        {attention ? t('family_attention_sub') : t('family_all_calm')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  {/* Device health */}
                  {dev ? (
                    <div className="flex items-center gap-4 flex-wrap text-sm" data-testid={`family-health-${p.id}`}>
                      <span className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
                        {t('device_personal_alarm')}
                      </span>
                      <StatusChip status={dev.connectivity_status || (dev.online ? 'online' : 'offline')} />
                      <span className={`inline-flex items-center gap-1 ${batteryColor(dev.battery)}`}>
                        {dev.battery != null ? `${dev.battery}%` : '—'}
                        {dev.charging && <Lightning size={13} weight="fill" className="text-sage-600" />}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-ink-muted">
                        <SignalBars score={dev.signal_quality} />
                      </span>
                      {dev.last_seen && (
                        <span className="text-ink-muted inline-flex items-center gap-1">
                          <Clock size={13} /> {t('family_last_seen')} {rel(dev.last_seen)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-ink-muted">{t('family_no_device')}</p>
                  )}

                  {/* Location */}
                  {dev && <DeviceLocationMap deviceId={dev.id} testid={`family-location-${p.id}`} />}

                  {presence[p.id] && (
                    <div className="rounded-2xl bg-bg-alt/60 border border-stone_line p-5 sm:p-6" data-testid={`family-movement-${p.id}`}>
                      <MovementOverview bare devices={presence[p.id]} name={(p.name || '').split(' ')[0]} scope={{ patient_id: p.id }} testid={`family-movement-overview-${p.id}`} />
                    </div>
                  )}

                  {/* Latest alert */}
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2">
                      {t('family_latest_alert')}
                    </div>
                    {latest ? (
                      <button
                        onClick={() => setDrawerId(latest.id)}
                        data-testid={`family-alert-${p.id}`}
                        className="w-full text-left bg-bg-alt hover:bg-sage-50 rounded-xl px-4 py-3 flex flex-col items-start sm:flex-row sm:items-center gap-2 sm:gap-3 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-ink" data-testid={`family-alert-text-${p.id}`}>{alertText(latest)}</div>
                          <div className="text-xs text-ink-muted mt-0.5">{rel(latest.created_at)}</div>
                        </div>
                        <span className="inline-flex items-center gap-1 text-xs text-sage-700 font-medium whitespace-nowrap">
                          {t('family_view_details')} <CaretRight size={13} weight="bold" />
                        </span>
                      </button>
                    ) : (
                      <p className="text-sm text-ink-soft">{t('family_no_alerts')}</p>
                    )}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}

      {drawerId && (
        <AlertDrawer alertId={drawerId} alert={Object.values(alertsByRes).flat().find((a) => a.id === drawerId) || null} onClose={() => setDrawerId(null)} onChanged={load} />
      )}
    </div>
  );
}
